/**
 * Station workTime helpers.
 *
 * Legacy API/DB often stores only `{ monday: ['ANYTIME'] }` meaning 24/7 all week.
 * New admin UI uses AllDay / NotWork / [from, to] for each weekday.
 *
 * The edit form Select falls back to displaying "AllDay" when a day value is
 * missing/empty — without normalizing on load, save validation fails with
 * "Set working hours for each day" even though the UI looks filled.
 */

export const WORK_TIME_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export function getDefaultWorkTime() {
  return WORK_TIME_DAYS.reduce((acc, day) => ({ ...acc, [day]: ['AllDay'] }), {});
}

/**
 * Normalize API/DB workTime into a full 7-day form model.
 * - Missing/empty days → ['AllDay'] (matches Select display fallback)
 * - ANYTIME → AllDay
 */
export function normalizeWorkTimeForForm(workTime) {
  const src = workTime && typeof workTime === 'object' ? workTime : {};

  return WORK_TIME_DAYS.reduce((acc, day) => {
    const arr = src[day];
    if (!Array.isArray(arr) || arr.length === 0 || arr[0] == null || String(arr[0]).trim() === '') {
      acc[day] = ['AllDay'];
      return acc;
    }
    if (arr[0] === 'ANYTIME' || arr[0] === 'AllDay') {
      acc[day] = ['AllDay'];
      return acc;
    }
    if (arr[0] === 'NotWork') {
      acc[day] = ['NotWork'];
      return acc;
    }
    if (arr.length >= 2 && arr[0] && arr[1]) {
      acc[day] = [String(arr[0]), String(arr[1])];
      return acc;
    }
    acc[day] = ['AllDay'];
    return acc;
  }, {});
}

export function isAllDaySchedule(dayValue) {
  return Array.isArray(dayValue) && (dayValue[0] === 'AllDay' || dayValue[0] === 'ANYTIME');
}

export function isNotWorkSchedule(dayValue) {
  return Array.isArray(dayValue) && dayValue[0] === 'NotWork';
}

export function isCustomSchedule(dayValue) {
  return (
    Array.isArray(dayValue) &&
    dayValue.length >= 2 &&
    dayValue[0] !== 'AllDay' &&
    dayValue[0] !== 'NotWork' &&
    dayValue[0] !== 'ANYTIME'
  );
}

/** True when every weekday has a non-empty schedule entry. */
export function isWorkTimeComplete(workTime) {
  if (!workTime || typeof workTime !== 'object') return false;
  return WORK_TIME_DAYS.every((day) => {
    const arr = workTime[day];
    return Array.isArray(arr) && arr.length > 0 && arr[0] != null && String(arr[0]).trim() !== '';
  });
}
